import { useParams } from '@tanstack/react-router'
import { useRolodexEntries } from 'features/rolodex'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

interface OrgSiteContextValue {
  readonly orgId: string | null
  readonly siteId: string | null
  readonly orgEntry: GeckoEntry | null
  readonly siteEntry: GeckoEntry | null
  readonly isLoading: boolean
  readonly displayName: string
}

const OrgSiteContext = createContext<OrgSiteContextValue | null>(null)

interface OrgSiteProviderProps {
  readonly children: React.ReactNode
}

/**
 * Provider component that resolves and manages organization and site context based on URL parameters.
 *
 * This provider:
 * - Extracts an ID from URL params that could be either an organization or site ID
 * - Queries the rolodex to determine the entry type
 * - Resolves the organization ID and site ID accordingly:
 *   - If the ID is an organization, sets orgId and leaves siteId as null
 *   - If the ID is a site, sets siteId and infers the organization from tags
 *   - If no ID is present, both are null (global view)
 * - Provides access to the full GeckoEntry objects for both org and site
 * - Computes a display name based on the current context (site name, org name, or "All Sites")
 *
 * @example
 * // Wrap your components that need org/site context
 * <OrgSiteProvider>
 *   <YourComponent />
 * </OrgSiteProvider>
 *
 * // Access the context in child components
 * const { orgId, siteId, displayName } = useOrgSite()
 */
export const OrgSiteProvider: React.FC<OrgSiteProviderProps> = ({
  children,
}) => {
  const { orgOrSiteID: orgOrSiteIdFromUrl } = useParams({ strict: false })

  const [orgId, setOrgId] = useState<string | null>(null)
  const [siteId, setSiteId] = useState<string | null>(null)

  const { entries: entriesFromUrl, isLoading } = useRolodexEntries(
    useMemo(
      () => ({ ids: orgOrSiteIdFromUrl ? [orgOrSiteIdFromUrl] : [] }),
      [orgOrSiteIdFromUrl]
    )
  )

  useEffect(() => {
    if (orgOrSiteIdFromUrl && entriesFromUrl && entriesFromUrl.length > 0) {
      let resolvedOrgId: string | null = null
      let resolvedSiteId: string | null = null

      const entry = entriesFromUrl[0]

      if (entry.type === RolodexEntryType.ORGANIZATION) {
        resolvedOrgId = entry.id
      } else if (entry.type === RolodexEntryType.SITE) {
        resolvedSiteId = entry.id
        resolvedOrgId = entry.tags.organization ?? null
      }

      setOrgId(resolvedOrgId)
      setSiteId(resolvedSiteId)
    } else if (!orgOrSiteIdFromUrl) {
      setOrgId(null)
      setSiteId(null)
    }
  }, [orgOrSiteIdFromUrl, entriesFromUrl])

  const orgEntry = useMemo(() => {
    if (orgId && entriesFromUrl && entriesFromUrl.length > 0) {
      const entry = entriesFromUrl[0]
      return entry.type === RolodexEntryType.ORGANIZATION ? entry : null
    }
    return null
  }, [orgId, entriesFromUrl])

  const siteEntry = useMemo(() => {
    if (siteId && entriesFromUrl && entriesFromUrl.length > 0) {
      const entry = entriesFromUrl[0]
      return entry.type === RolodexEntryType.SITE ? entry : null
    }
    return null
  }, [siteId, entriesFromUrl])

  const displayName = useMemo(() => {
    if (siteEntry) {
      return `${siteEntry.data.name ?? siteId}`
    } else if (orgEntry) {
      return `${orgEntry.data.name ?? orgId}`
    }
    return 'All Sites'
  }, [siteEntry, orgEntry, siteId, orgId])

  const contextValue: OrgSiteContextValue = useMemo(
    () => ({
      orgId,
      siteId,
      orgEntry,
      siteEntry,
      isLoading,
      displayName,
    }),
    [orgId, siteId, orgEntry, siteEntry, isLoading, displayName]
  )

  return (
    <OrgSiteContext.Provider value={contextValue}>
      {children}
    </OrgSiteContext.Provider>
  )
}

export const useOrgSite = (): OrgSiteContextValue => {
  const context = useContext(OrgSiteContext)
  if (!context) {
    throw new Error('useOrgSite must be used within an OrgSiteProvider')
  }
  return context
}
